import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  /** Missing/auto follows browser; explicit choice is saved on the server. */
  language?: string | null;
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: string;
  /**
   * Tenant-level onboarding completion flag. The dashboard auth guard reads
   * this to push admins through `/onboarding` until the wizard succeeds. The
   * field is optional because old persisted sessions (predating the wizard)
   * won't have it; a missing value is treated as "completed" so we never
   * trap a non-admin user in onboarding.
   */
  onboarding_completed?: boolean;
}

/**
 * Snapshot of the operator's super-admin identity, kept around while
 * impersonating a tenant so we can restore it without a re-login. The
 * impersonation token replaces `token` in the active session; this
 * stash holds the original credentials for the eventual restore.
 */
interface ImpersonationStash {
  /** Super-admin access token before impersonation began. */
  originalToken: string;
  originalRefreshToken: string;
  originalUser: User;
  /** Display name of the tenant we're operating as — shown in the banner. */
  tenantName: string;
  /** Tenant id we're operating as — used by the banner / API calls. */
  tenantId: string;
  /** ISO timestamp when the impersonation token expires (default 1h). */
  expiresAt?: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  /** Non-null when a super_admin is currently impersonating a tenant. */
  impersonation: ImpersonationStash | null;
  setAuth: (token: string, refreshToken: string, user: User) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<User>) => void;
  /**
   * Begin operating as a tenant. Stashes the current super_admin
   * credentials and swaps in the impersonation token. Reload-resilient
   * because the entire state is persisted.
   */
  startImpersonation: (args: {
    impersonationToken: string;
    tenantId: string;
    tenantName: string;
    expiresAt?: string;
  }) => void;
  /** Restore the original super_admin credentials and clear the stash. */
  endImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      impersonation: null,
      setAuth: (token, refreshToken, user) =>
        set({ token, refreshToken, user, impersonation: null }),
      clearAuth: () =>
        set({ token: null, refreshToken: null, user: null, impersonation: null }),
      updateUser: (user) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...user } : null,
        })),
      startImpersonation: ({ impersonationToken, tenantId, tenantName, expiresAt }) =>
        set((state) => {
          // Refuse if already impersonating — should be ended first to
          // avoid losing the original token. Defensive against UI bugs
          // that double-fire the action.
          if (state.impersonation) return state;
          if (!state.token || !state.user) return state;
          return {
            impersonation: {
              originalToken: state.token,
              originalRefreshToken: state.refreshToken ?? '',
              originalUser: state.user,
              tenantId,
              tenantName,
              expiresAt,
            },
            // Active session now points at the tenant. The user object
            // stays as the super_admin's display info so the topbar
            // shows "you" — the banner makes the tenant context clear.
            token: impersonationToken,
            // Refresh tokens don't apply to impersonation tokens (1h
            // hard expiry); set null so the SDK refresh interceptor
            // doesn't try.
            refreshToken: null,
          };
        }),
      endImpersonation: () =>
        set((state) => {
          if (!state.impersonation) return state;
          return {
            token: state.impersonation.originalToken,
            refreshToken: state.impersonation.originalRefreshToken || null,
            user: state.impersonation.originalUser,
            impersonation: null,
          };
        }),
    }),
    {
      name: 'blaze-auth',
    }
  )
);
